import {
    claimItemListView,
    noteDataLayerEventCommitted,
    queueDataLayerEvent,
    shouldQueueDataLayerEvent,
    takeReadyQueuedDataLayerEvents,
} from './data-layer-bootstrap';

export type DataLayerItem = {
    item_id: string;
    item_name: string;
    affiliation?: string;
    coupon?: string;
    discount: number;
    index: number;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    price: number;
    quantity: number;
};

export type DataLayerEcommerce = {
    currency?: string;
    payment_type?: string;
    shipping_method?: string;
    shipping_tier?: string;
    value?: number;
    item_list_id?: string;
    item_list_name?: string;
    transaction_id?: string;
    tax?: number;
    shipping?: number;
    customer_type?: 'new' | 'returning';
    items: DataLayerItem[];
};

export type DataLayerUserData = {
    first_name?: string;
    last_name?: string;
    email_address?: string;
    phone_number?: string;
    street?: string;
    city?: string;
    region?: string;
    country?: string;
    zip_code?: string;
};

/** Shop online and/or Eataly Club enrollment as pushed in `log_state.user_info`. */
export type DataLayerMembershipStatus = 'shop' | 'club' | 'shop_club' | 'none' | 'unknown';

export type DataLayerUserInfo = DataLayerUserData & {
    id: string;
    type: 'member' | 'guest';
    marketing_consent?: boolean;
    consent_state: 'G100' | 'G101' | 'G110' | 'G111';
    membership_status?: DataLayerMembershipStatus;
    browsing_preference?: 'product' | 'class' | 'event' | 'restaurant';
    first_order_date?: string;
    last_order_date?: string;
    orders_count?: number;
    total_spent?: number;
};

export interface DataLayerEventPayloads {
    page_view: {
        language: string;
        country: string;
        content_group: string;
        http_status_code: number;
        page_location: string;
        page_title: string;
    };
    log_state: { user_info: DataLayerUserInfo };
    view_item: { ecommerce: DataLayerEcommerce };
    view_item_list: { ecommerce: DataLayerEcommerce };
    select_item: { ecommerce: DataLayerEcommerce };
    add_to_cart: { ecommerce: DataLayerEcommerce };
    remove_from_cart: { ecommerce: DataLayerEcommerce };
    add_to_wishlist: { ecommerce: DataLayerEcommerce };
    view_cart: { ecommerce: DataLayerEcommerce };
    begin_checkout: { ecommerce: DataLayerEcommerce };
    add_shipping_info: {
        ecommerce: DataLayerEcommerce;
        user_data: DataLayerUserData;
    };
    add_payment_info: {
        ecommerce: DataLayerEcommerce;
        user_data: DataLayerUserData;
    };
    purchase: {
        ecommerce: DataLayerEcommerce;
        purchase_category: 'product' | 'class' | 'event' | 'mixed';
        user_data: DataLayerUserData;
    };
    search: { search_term: string; search_result_count: number };
    filter_click: { filter_type: string; filter_action: 'select' | 'remove'; filter_value: string | null };
    sort_by_click: { sort_type: string };
    tab_click: { click_text: string };
    home_box_click: { click_element: string; click_text: string; click_url: string };
    nav_cta_click: { click_text: string; click_location: string; click_url: string };
    register_click: { click_text: string; click_url: string };
    login: {
        method: 'google' | 'apple' | 'email' | 'magic_link';
        target: 'shop' | 'club';
        user_data: DataLayerUserData;
    };
    sign_up: {
        method: 'google' | 'apple' | 'email' | 'magic_link';
        target: 'shop' | 'club';
        user_data: DataLayerUserData;
    };
    newsletter_subscription: { user_data: DataLayerUserData };
    generate_lead: { form_name: string; user_data: DataLayerUserData };
    share: {
        method: 'whatsapp' | 'copy link' | 'email' | 'facebook' | 'twitter' | 'linkedin' | 'native';
        content_type: 'class' | 'event' | 'club';
        item_id: string;
    };
    phone_click: { click_text: string; click_url: string };
    mail_click: { click_text: string; click_url: string };
    get_direction_click: { click_text: string; click_url: string };
    eataly_club_click: { click_text: string; click_url: string };
    book_a_table_click: { restaurant_name: string; click_url: string };
    opentable_widget_opened: { restaurant_name: string; location_name: string };
    menu_click: { restaurant_name: string; click_url: string };
    restaurant_click: { restaurant_name: string; click_url: string };
    wine_list_click: { restaurant_name: string; click_url: string };
    store_click: { store_name: string; click_url: string };
    store_outbound_click: { store_name: string; outbound: true; click_url: string };
    flyer_click: { click_text: string; store_name: string; click_url: string };
}

export type DataLayerEventName = keyof DataLayerEventPayloads;

export type EcommerceDataLayerEventName =
    | 'view_item'
    | 'view_item_list'
    | 'select_item'
    | 'add_to_cart'
    | 'remove_from_cart'
    | 'add_to_wishlist'
    | 'view_cart'
    | 'begin_checkout'
    | 'add_shipping_info'
    | 'add_payment_info'
    | 'purchase';

type EventTypeFor<TName extends DataLayerEventName> = TName extends EcommerceDataLayerEventName ? 'ga4_eec' : 'ga4_ce';

export type DataLayerEvent<TName extends DataLayerEventName = DataLayerEventName> = TName extends DataLayerEventName
    ? {
          event: TName;
          event_type: EventTypeFor<TName>;
      } & DataLayerEventPayloads[TName]
    : never;

export type DataLayerEntry = DataLayerEvent | DataLayerReset | DataLayerLogStateReset;

/**
 * GTM retains values between pushes. Every event starts with the explicit reset
 * required by the Eataly tracking specification.
 *
 * - `user_info` is reset only before `log_state`.
 * - Page-level `language`, `country`, `content_group`, and `http_status_code` are set on
 *   each route change via `page_view` and are not cleared by the generic reset so later
 *   events on the same page retain the current page context (distinct from `user_info.country`).
 */
export type DataLayerReset = {
    ecommerce: null;
    user_data: null;
    item_id: null;
    content_type: null;
    method: null;
    search_term: null;
    search_result_count: null;
    shipping_tier: null;
    payment_type: null;
    click_element: null;
    click_location: null;
    click_text: null;
    click_url: null;
    filter_type: null;
    filter_action: null;
    filter_value: null;
    form_name: null;
    outbound: null;
    customer_type: null;
    purchase_category: null;
    restaurant_name: null;
    shipping_method: null;
    sort_type: null;
    store_name: null;
    target: null;
    event_type: null;
};

export type DataLayerLogStateReset = DataLayerReset & {
    user_info: null;
};

export const DATA_LAYER_EVENT_RESET: Readonly<DataLayerReset> = Object.freeze({
    ecommerce: null,
    user_data: null,
    item_id: null,
    content_type: null,
    method: null,
    search_term: null,
    search_result_count: null,
    shipping_tier: null,
    payment_type: null,
    click_element: null,
    click_location: null,
    click_text: null,
    click_url: null,
    filter_type: null,
    filter_action: null,
    filter_value: null,
    form_name: null,
    outbound: null,
    customer_type: null,
    purchase_category: null,
    restaurant_name: null,
    shipping_method: null,
    sort_type: null,
    store_name: null,
    target: null,
    event_type: null,
});

export type DataLayerGtmBootstrap = {
    'gtm.start': number;
    event: 'gtm.js';
};

declare global {
    interface Window {
        dataLayer?: Array<DataLayerEntry | DataLayerGtmBootstrap>;
    }
}

/** `log_state.user_info` only: location fields stay readable per tracking plan. */
const USER_INFO_HASHED_KEYS = [
    'first_name',
    'last_name',
    'email_address',
    'phone_number',
    'street',
] as const satisfies readonly (keyof DataLayerUserData)[];

const SHA_256_HEX_PATTERN = /^[a-f0-9]{64}$/i;
let pendingDataLayerPush: Promise<void> = Promise.resolve();

export async function hashDataLayerValue(value: string): Promise<string> {
    const normalizedValue = value.trim();
    if (!normalizedValue || SHA_256_HEX_PATTERN.test(normalizedValue)) {
        return normalizedValue.toLowerCase();
    }

    if (!globalThis.crypto?.subtle) {
        return '';
    }

    const bytes = new TextEncoder().encode(normalizedValue);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function hashUserData(userData: DataLayerUserData): Promise<DataLayerUserData> {
    const hashedData: DataLayerUserData = { ...userData };

    await Promise.all(
        USER_INFO_HASHED_KEYS.map(async (key) => {
            const value = userData[key];
            if (value !== undefined) {
                hashedData[key] = await hashDataLayerValue(key === 'email_address' ? value.toLowerCase() : value);
            }
        })
    );

    return hashedData;
}

async function protectEventPii<TName extends DataLayerEventName>(
    event: DataLayerEvent<TName>
): Promise<DataLayerEvent<TName>> {
    const eventRecord = event as DataLayerEvent<TName> & {
        user_data?: DataLayerUserData;
        user_info?: DataLayerUserInfo;
    };
    const protectedUserData = eventRecord.user_data ? await hashUserData(eventRecord.user_data) : undefined;
    const protectedUserInfo = eventRecord.user_info
        ? ({
              ...eventRecord.user_info,
              ...(await hashUserData(eventRecord.user_info)),
          } satisfies DataLayerUserInfo)
        : undefined;

    return {
        ...event,
        ...(protectedUserData ? { user_data: protectedUserData } : {}),
        ...(protectedUserInfo ? { user_info: protectedUserInfo } : {}),
    } as DataLayerEvent<TName>;
}

function enqueueCommittedPush<TName extends DataLayerEventName>(event: DataLayerEvent<TName>): Promise<void> {
    const push = async () => {
        const protectedEvent = await protectEventPii(event);
        const dataLayer = (window.dataLayer ??= []);
        const reset: DataLayerReset | DataLayerLogStateReset =
            event.event === 'log_state'
                ? { ...DATA_LAYER_EVENT_RESET, user_info: null }
                : { ...DATA_LAYER_EVENT_RESET };
        dataLayer.push(reset);
        dataLayer.push(protectedEvent);
        noteDataLayerEventCommitted(event);
    };
    const queuedPush = pendingDataLayerPush.then(push, push);
    pendingDataLayerPush = queuedPush.catch(() => undefined);
    return queuedPush.then(() => flushQueuedDataLayerEvents());
}

function flushQueuedDataLayerEvents(): Promise<void> {
    const ready = takeReadyQueuedDataLayerEvents();
    if (ready.length === 0) {
        return Promise.resolve();
    }

    return ready.reduce(
        (chain, item) => chain.then(() => enqueueCommittedPush(item.event).then(item.resolve, item.reject)),
        Promise.resolve()
    );
}

export function pushDataLayerEvent<TName extends DataLayerEventName>(event: DataLayerEvent<TName>): Promise<void> {
    if (typeof window === 'undefined') {
        return Promise.resolve();
    }

    if (event.event === 'view_item_list') {
        const { item_list_id: itemListId, items } = (event as DataLayerEvent<'view_item_list'>).ecommerce;
        const listKey = `${itemListId ?? ''}|${items.map((item) => `${item.index}:${item.item_id}`).join(',')}`;
        if (!claimItemListView(listKey)) {
            return Promise.resolve();
        }
    }

    if (shouldQueueDataLayerEvent(event)) {
        return queueDataLayerEvent(event);
    }

    return enqueueCommittedPush(event);
}
